import React, { useState, useEffect, useRef } from "react";
import Header from "../../Components/Header";
import Seo from "../../Components/SeoComponent/Seo";
import Loader from "../../Loader";
import { FaEdit, FaPlus, FaSearch, FaTimes, FaTrash } from "react-icons/fa";
import { numberToWords } from "../../utils";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { CreateBulkPayout } from "../../Services/PayoutService.js/Payouts";
import PaginationData from "../../Components/PaginationData";
import DataModal from "../../Components/ConfirmPopUp/DataModal";
import { fetchProfile } from "../../Services/profile/profileservice";
import { FaInfoCircle } from "react-icons/fa";
import html2pdf from "html2pdf.js";
import { IoMdDownload } from "react-icons/io";
import {
  BeneficiaryGroupReports,
  fetchBeneficiaryDetailsByName,
  fetchBeneficiaryDetailsBySearch,
} from "../../Services/PayoutService.js/Payouts";

const BulkPayout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [pin, setPin] = useState("");
  const inputRefs = useRef(
    Array.from({ length: 6 }).map(() => React.createRef()),
  );
  const [searchText, setSearchText] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [payoutList, setPayoutList] = useState([]); // This will store the final payout list
  const [profileData, setProfileData] = useState([]);
  const [packageData, setPackageData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [debitType, setDebitType] = useState("multiple");
  const [payoutAccount, setPayoutAccount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [amount, setAmount] = useState("");
  const [transferType, setTransferType] = useState("");
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [isOpenView, setIsOpenView] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [slipData, setSlipData] = useState([]);

  // Form state for adding beneficiaries
  const [formData, setFormData] = useState({
    beneficiary_group_id: "",
  });
  const [tableData, setTableData] = useState([]);

  // Calculate total amount whenever payout list changes
  useEffect(() => {
    const total = payoutList.reduce(
      (acc, curr) => acc + Number(curr.amount || 0),
      0,
    );
    setTotalAmount(total);
  }, [payoutList]);

  const handleAmountChange = (id, value) => {
    if (value < 0) return;
    setPayoutList((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, amount: Number(value) } : item,
      ),
    );
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "beneficiary_group_id" && value) {
      try {
        setIsLoading(true);
        const response = await fetchBeneficiaryDetailsByName(value);
        if (response?.data) {
          setTableData(response.data.beneficiaries);
        }
      } catch (error) {
        showErrorToast("Failed to fetch beneficiary details");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Retrieve Group Data
  const RetrieveGroupData = async () => {
    try {
      setIsLoading(true);
      const response = await BeneficiaryGroupReports();
      if (response || response.data) {
        setPackageData(response.data || []);
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    RetrieveGroupData();
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchText.trim() !== "") {
        fetchSearchResults(searchText);
      } else {
        setSearchData([]);
        setSelectedBeneficiary(null);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const fetchSearchResults = async (search) => {
    try {
      const response = await fetchBeneficiaryDetailsBySearch(search);
      if (response?.data?.length > 0) {
        const filtered = response.data.filter((item) => {
          const lower = search.toLowerCase();
          return (
            item.name?.toLowerCase().includes(lower) ||
            item.account_number?.includes(search) ||
            item.ifsc_code?.toLowerCase().includes(lower)
          );
        });
        setSearchData(filtered);
      } else {
        setSearchData([]);
      }
    } catch (error) {
      showErrorToast("Search failed", error);
      setSearchData([]);
    }
  };

  useEffect(() => {
    async function fetchProfileData() {
      try {
        const response = await fetchProfile();
        setProfileData(response.data);
      } catch (error) {
        showErrorToast("Error fetching profile data", error);
      }
    }
    fetchProfileData();
  }, []);

  // Add selected beneficiary to payout list
  const handleAddToPayout = () => {
    if (!selectedBeneficiary) {
      showErrorToast("Please select a beneficiary");
      return;
    }
    if (!amount || amount <= 0) {
      showErrorToast("Please enter a valid amount");
      return;
    }
    if (!transferType) {
      showErrorToast("Please select a transfer type");
      return;
    }

    // Check if beneficiary already exists in payout list
    const exists = payoutList.find(
      (item) => item.account_number === selectedBeneficiary.account_number,
    );
    if (exists) {
      showErrorToast("Beneficiary already added to payout list");
      return;
    }

    const newPayout = {
      id: Date.now(), // Generate unique ID
      name: selectedBeneficiary.name,
      account_number: selectedBeneficiary.account_number,
      ifsc_code: selectedBeneficiary.ifsc_code,
      bank_name: selectedBeneficiary.bank_name,
      branch_name: selectedBeneficiary.branch_name,
      amount: parseFloat(amount),
      transfer_type: transferType,
      remarks: selectedBeneficiary.description || remarks,
      email: selectedBeneficiary.emailId,
      mobile: selectedBeneficiary.mobileNo,
      address: selectedBeneficiary.beneficiaryAddress,
      beneficiary_id: selectedBeneficiary.id,
      transaction_date: new Date().toISOString().split("T")[0],
    };

    setPayoutList((prev) => [...prev, newPayout]);

    // Reset form
    setSelectedBeneficiary(null);
    setSearchText("");
    setAmount("");
    setTransferType("");

    showSuccessToast("Beneficiary added to payout list successfully");
  };

  // Remove beneficiary from payout list
  const handleRemoveFromPayout = (id) => {
    setPayoutList((prev) => prev.filter((item) => item.id !== id));
    showSuccessToast("Beneficiary removed from payout list");
  };

  const handleBulkPayout = async () => {
    if (payoutList.length === 0) {
      showErrorToast("No beneficiaries added to payout list");
      return;
    }

    try {
      setIsLoading(true);

      const bulkData = payoutList.map((payout) => ({
        amount: String(payout.amount),
        transactionType: payout.transfer_type.toUpperCase(),
        paymentDescription: payout.remarks,
        creditAccountNumber: payout.account_number,
        remitterName: payout.name,
        beneficiaryIFSC: payout.ifsc_code,
        beneficiaryName: payout.name,
        beneficiaryAddress: payout.address ?? "", // Add this
        emailId: payout.email ?? "", // Add this
        mobileNo: payout.mobile ?? "",
      }));

      const payoutData = {
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        // payment_transfer_type: "BULK",
        payout_account_number: payoutAccount || "",
        bulkData: bulkData,
      };
      const response = await CreateBulkPayout(payoutData);
      const statusCode = response?.status || response?.status?.code;

      if (statusCode === 422) {
        setIsLoading(false);

        const errorData = response?.data || response;

        const errorMsg =
          errorData?.message ||
          errorData?.status?.message ||
          errorData?.error ||
          "An unknown error occurred.";

        showErrorToast(errorMsg);
        return;
      }

      if (statusCode === 404) {
        setIsLoading(false);

        const errorData = response?.data || response;

        const errorMsg =
          errorData?.message ||
          errorData?.status?.message ||
          errorData?.error ||
          "An unknown error occurred.";

        showErrorToast(errorMsg);
        return;
      }

      if (statusCode || response.code === 200) {
        showSuccessToast(
          response?.message ||
            response.data.message ||
            "Bulk payout created successfully",
        );
        const allSlipData = Array.isArray(response.data)
          ? response.data.map((item) => item?.data)
          : [];
        setSlipData(allSlipData);

        setPayoutList([]);
        setPin("");
        setPayoutAccount("");
        setRemarks("");
        setIsOpenView(false);

        setTimeout(() => {
          setIsOpen(true);
        }, 600);
      } else {
        const errorMessage =
          response?.status?.message ||
          response?.message ||
          "Failed to create bulk payout";
        showErrorToast(errorMessage);
      }
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";
      showErrorToast("Error processing bulk payout: " + serverMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (profileData?.payout_account?.account_number) {
      setPayoutAccount(profileData.payout_account.account_number);
    }
  }, [profileData]);

  const handleDownload = () => {
    const element = document.getElementById("txn-slip");

    const opt = {
      margin: 10,
      filename: "All_Transaction_Slips.pdf",
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 3,
        useCORS: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: { mode: ["css", "legacy"] }, // 👈 Important to break across pages
    };

    html2pdf().set(opt).from(element).save();
  };

  const availableBalance =
    parseFloat(profileData?.payout_account?.balance) || 0;
  const isAmountValid = totalAmount <= availableBalance;

  const combinedBeneficiaries = [...payoutList, ...tableData];

  const normalizeBeneficiaries = [
    ...payoutList.map((item) => ({
      ...item,
      code: item.code || "—",
      bank_name: item.bank_name || "—",
      branch_name: item.branch_name || "—",
      is_vendor: item.is_vendor ?? false,
      is_salary: item.is_salary ?? false,
      paymentType: item.transfer_type || "NEFT",
    })),
    ...tableData,
  ];

  return (
    <div>
      <Header heading={"On Screen Bulk Payments"} />
      <Seo title={"Bulk Payments"} />
      {isLoading && <Loader />}
      <div>
        <DataModal
          onClose={() => setIsOpenView(false)}
          visible={isOpenView}
          className="!p-0"
        >
          <div className="w-full rounded-md overflow-hidden">
            {/* Header */}
            <div className="bg-blue-900 text-white px-2 py-1 font-bold text-lg">
              Payment Confirmation
            </div>

            {/* Payment Details */}
            <div className="px-3 py-2 bg-white">
              <h4 className="text-gray-800 text-lg font-semibold mb-2">
                Payment Details
              </h4>
              <div className="space-y-1 text-sm text-gray-700">
                <p>
                  <span className="font-semibold">Total Accounts:</span>{" "}
                  {payoutList.length}
                </p>
                <p>
                  <span className="font-semibold">Pay Amount:</span> ₹
                  {totalAmount.toFixed(2)}
                </p>
                <p>
                  <span className="font-semibold">Amount in words:</span>{" "}
                  {numberToWords(totalAmount)} Rupees
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="px-3 py-2 bg-gray-100 flex justify-end gap-2">
              <button
                type="button"
                className="bg-red-600 text-white px-2 py-1 rounded-md font-semibold hover:bg-red-700 transition duration-200 shadow-md"
                onClick={() => setIsOpenView(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bg-blue-700 text-white px-3 py-1 rounded-md font-semibold hover:bg-blue-800 transition duration-200 shadow-md"
                onClick={handleBulkPayout}
              >
                Pay Now
              </button>
            </div>
          </div>
        </DataModal>
        <DataModal
          visible={isOpen}
          onClose={() => setIsOpen(false)}
          className="!p-0"
        >
          <div className="w-full max-w-7xl p-1 sm:p-2 md:p-1 bg-white rounded-lg shadow-2xl relative">
            <div
              id="txn-slip"
              className="bg-gray-50 rounded-xl shadow-inner p-1 space-y-2 border border-gray-200"
            >
              <h2 className="text-center text-2xl font-bold text-blue-700 border-b border-gray-300 pb-2">
                Transaction Slip
              </h2>

              <div className="max-h-[70vh] overflow-y-auto pr-1">
                <div className="space-y-1">
                  {Array.isArray(slipData) && slipData.length > 0 ? (
                    slipData.map((slip, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-xl shadow-inner p-1 space-y-1 border border-gray-200 mb-4"
                      >
                        <h2 className="text-center text-xl font-bold text-blue-700 border-b border-gray-300 pb-1">
                          Transaction Slip {index + 1}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 px-2 text-sm text-gray-800">
                          {/* Now replace all slipData.XXX with slip.XXX */}

                          <div className="bg-white px-2 py-1 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              Status:
                            </span>
                            <br />
                            <span className="text-blue-600 font-medium">
                              {slip?.response
                                ?.initiateAuthGenericFundTransferAPIResp
                                ?.metaData?.status || "N/A"}
                            </span>
                          </div>

                          <div className="bg-white px-2 py-1 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              Date & Time:
                            </span>
                            <br />
                            <span>
                              {slip?.response
                                ?.initiateAuthGenericFundTransferAPIResp
                                ?.metaData?.time
                                ? new Date(
                                    slip.response
                                      .initiateAuthGenericFundTransferAPIResp
                                      .metaData.time,
                                  ).toLocaleString()
                                : "N/A"}
                            </span>
                          </div>

                          <div className="bg-white px-2 py-1 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              Transaction ID:
                            </span>
                            <br />
                            {slip?.response
                              ?.initiateAuthGenericFundTransferAPIResp
                              ?.resourceData?.transactionID || "N/A"}
                          </div>

                          <div className="bg-white px-2 py-1 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              UTR No:
                            </span>
                            <br />
                            {slip?.response
                              ?.initiateAuthGenericFundTransferAPIResp
                              ?.resourceData?.transactionReferenceNo || "N/A"}
                          </div>

                          <div className="bg-white px-2 py-1 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              Transaction Status:
                            </span>
                            <br />
                            <span className="text-green-600 font-semibold">
                              {slip?.response
                                ?.initiateAuthGenericFundTransferAPIResp
                                ?.resourceData?.status || "N/A"}
                            </span>
                          </div>

                          <div className="bg-white px-2 py-1 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              Txn Amount:
                            </span>
                            <br />
                            <span className="text-indigo-700 font-semibold">
                              ₹{slip?.details?.amount || "N/A"}
                            </span>
                          </div>

                          <div className="bg-white px-2 py-1 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              IFSC:
                            </span>
                            <br />
                            {slip?.details?.ifsc_code || "N/A"}
                          </div>

                          <div className="bg-white px-2 py-1 rounded shadow-sm border md:col-span-2">
                            <span className="font-semibold text-gray-600">
                              Beneficiary Name:
                            </span>
                            <br />
                            {slip?.details?.beneficiary_name || "N/A"}
                          </div>

                          <div className="bg-white p-3 rounded shadow-sm border md:col-span-2">
                            <span className="font-semibold text-gray-600">
                              Beneficiary Account:
                            </span>
                            <br />
                            {slip?.creditAccountNumber || "N/A"}
                          </div>

                          <div className="bg-white p-3 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              Message:
                            </span>
                            <br />
                            {slip?.response
                              ?.initiateAuthGenericFundTransferAPIResp?.metaData
                              ?.message || "N/A"}
                          </div>

                          <div className="bg-white p-3 rounded shadow-sm border">
                            <span className="font-semibold text-gray-600">
                              Remarks:
                            </span>
                            <br />
                            {slip?.details?.remarks || "N/A"}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 italic">
                      No Data Found
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-center gap-2 mt-2">
              <button
                onClick={handleDownload}
                className="bg-blue-600 hover:bg-blue-800 text-white px-2 py-1 rounded-md shadow transition"
              >
                <IoMdDownload size={16} className="inline mr-1" /> Download
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="bg-red-500 hover:bg-red-700 text-white px-2 py-1 rounded-md shadow transition"
              >
                ✕ Close
              </button>
            </div>
          </div>
        </DataModal>

        <div className="bg-white shadow-md p-2 rounded my-1 border border-gray-600 shadow-blue-700 w-full">
          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {/* Search Beneficiary */}
            <div className="w-full sm:w-1/2">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Search Beneficiary
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-400 rounded-md px-3">
                  <FaSearch className="text-gray-400 mr-2" />
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search By Name, Account No., IFSC"
                    className="h-10 px-2 w-full outline-none border-none"
                  />
                </div>

                {/* Dropdown Results */}
                {searchData.length > 0 && (
                  <ul className="absolute z-50 bg-white border border-gray-300 rounded w-full mt-1 max-h-60 overflow-y-auto shadow">
                    {searchData.map((item) => (
                      <li
                        key={item.id}
                        onClick={() => {
                          setSelectedBeneficiary(item);
                          setSearchText(
                            `${item.name} (${item.account_number}) (${item.ifsc_code})`,
                          );
                          setSearchData([]);
                        }}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        {item.name} ({item.account_number}) ({item.ifsc_code})
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Group Selection */}
            <div className="w-full sm:w-1/2">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Select Group
              </label>
              <select
                name="beneficiary_group_id"
                value={formData.beneficiary_group_id}
                onChange={handleChange}
                className="w-full border border-gray-400 rounded-md outline-none bg-white shadow-sm font-semibold px-2 py-2"
              >
                <option value="">Select Group</option>
                {packageData?.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Form Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2">
            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Account Number
              </label>
              <input
                type="text"
                className="h-10 px-2 border border-gray-400 rounded w-full outline-none bg-gray-100"
                readOnly
                value={selectedBeneficiary?.account_number || ""}
              />
            </div>

            {/* IFSC Code */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                IFSC Code
              </label>
              <input
                type="text"
                className="h-10 px-2 border border-gray-400 rounded w-full outline-none bg-gray-100"
                readOnly
                value={selectedBeneficiary?.ifsc_code || ""}
              />
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Amount
              </label>
              <input
                type="number"
                className="h-10 px-2 border border-gray-400 rounded w-full outline-none"
                placeholder="₹0.0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Date
              </label>
              <input
                type="date"
                className="h-10 px-2 border border-gray-400 rounded w-full outline-none bg-gray-100"
                readOnly
                value={
                  selectedBeneficiary?.created_at
                    ? new Date(selectedBeneficiary.created_at)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
              />
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Remarks
              </label>
              <input
                type="text"
                className="h-10 px-2 border border-gray-400 rounded w-full outline-none"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter remarks"
              />
            </div>

            {/* Transfer Type */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Transfer Type
              </label>
              <select
                className="h-10 px-2 border border-gray-400 rounded w-full outline-none bg-white"
                value={transferType}
                onChange={(e) => setTransferType(e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="neft">NEFT</option>
                <option value="imps">IMPS</option>
                <option value="rtgs">RTGS</option>
              </select>
            </div>

            {/* Add Button */}
            <div className="flex items-end">
              <button
                onClick={handleAddToPayout}
                className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-full w-1/2 flex justify-center items-center h-10"
              >
                <FaPlus />
              </button>
            </div>
          </div>
        </div>

        {normalizeBeneficiaries.length > 0 && (
          <div className="overflow-x-auto mt-4 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">All Beneficiaries</h3>
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="px-2 py-1 text-left border">SN</th>
                  <th className="px-2 py-1 text-left border">Code</th>
                  <th className="px-2 py-1 text-left border">Name</th>
                  <th className="px-2 py-1 text-left border">Account No</th>
                  <th className="px-2 py-1 text-left border">IFSC</th>
                  <th className="text-white px-2  py-1 border border-gray-300">
                    Amount(₹)
                  </th>
                  <th className="text-white px-2  py-1 border border-gray-300">
                    Payment Type
                  </th>
                  <th className="px-2 py-1 text-left border">Bank Name</th>
                  <th className="px-2 py-1 text-left border">Branch</th>
                  <th className="px-2 py-1 text-left border">Is Vendor</th>
                  <th className="px-2 py-1 text-left border">Is Salary</th>
                  <th className="px-2 py-1 text-left border">Action</th>
                </tr>
              </thead>
              <tbody>
                {normalizeBeneficiaries.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`border-t ${index % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-gray-100`}
                  >
                    <td className="px-2 py-1 border">{index + 1}</td>
                    <td className="px-2 py-1 border">{item.code || "—"}</td>
                    <td className="px-2 py-1 border">{item.name}</td>
                    <td className="px-2 py-1 border">{item.account_number}</td>
                    <td className="px-2 py-1 border">{item.ifsc_code}</td>
                    <td className="border px-2 py-1">
                      <input
                        type="number"
                        value={item.amount}
                        min={0}
                        onChange={(e) =>
                          handleAmountChange(item.id, e.target.value)
                        }
                        className="w-full outline-none px-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <select
                        className="bg-white outline-none border shadow-md"
                        value={item.paymentType}
                        // onChange={(e) => hand(item.id, e.target.value)}
                      >
                        <option value="NEFT">NEFT</option>
                        <option value="IMPS">IMPS</option>
                        <option value="RTGS">RTGS</option>
                      </select>
                    </td>
                    <td className="px-2 py-1 border">{item.bank_name}</td>
                    <td className="px-2 py-1 border">{item.branch_name}</td>
                    <td className="px-2 py-1 border">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${item.is_vendor ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {item.is_vendor ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-2 py-1 border">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${item.is_salary ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {item.is_salary ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-2 py-1 border">
                      <button
                        onClick={() => handleRemoveFromPayout(item.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats Section */}
        <div className="bg-slate-200 my-3 shadow-md shadow-blue-100 border p-4 stats flex flex-wrap gap-4">
          <div className="flex flex-col gap-2 items-center">
            <label
              htmlFor="accountSelect"
              className="font-semibold text-gray-800"
            >
              Select Account:
            </label>
            <input
              id="accountSelect"
              type="text"
              value={payoutAccount}
              readOnly
              className="rounded border border-gray-400 outline-none px-2 py-1 bg-gray-100 cursor-not-allowed"
            />

            <div className="text-right mt-2 mr-2">
              <p className="text-sm font-medium">
                Total Payout Amount:{" "}
                <span
                  className={`font-semibold ${isAmountValid ? "text-green-600" : "text-red-600"}`}
                >
                  ₹{totalAmount.toFixed(2)}
                </span>
              </p>
              <div className="flex items-center gap-2 text-sm">
                <p className="m-0">
                  Avl. Balance:{" "}
                  <span className="font-semibold text-blue-600">
                    ₹{availableBalance.toFixed(2)}
                  </span>
                </p>

                {/* Tooltip Wrapper */}
                <div className="relative group">
                  <FaInfoCircle
                    className="cursor-pointer text-blue-700"
                    size={24}
                  />

                  {/* Tooltip */}
                  <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 w-60 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 text-center">
                    The Payout Account balance will be used to send money to
                    vendor, BBPS commission, Salary Payment only.
                  </div>
                </div>
              </div>

              {!isAmountValid && (
                <p className="text-sm text-red-500 font-semibold">
                  ⚠ Total payout exceeds available balance.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="remarksInput"
              className="font-semibold text-gray-800 text-left"
            >
              Final Remarks:
            </label>
            <input
              type="text"
              id="remarksInput"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter global remarks"
              className="rounded text-left border border-gray-400 outline-none h-10 min-w-[200px] px-2"
            />
          </div>

          <div className="flex flex-wrap gap-4 items-center">
            <label className="inline-flex items-center gap-1">
              <input
                type="radio"
                name="debitType"
                value="multiple"
                checked={debitType === "multiple"}
                onChange={() => setDebitType("multiple")}
              />
              Multiple Debit
            </label>
          </div>

          <div className="flex justify-between items-center ml-auto">
            <div className="font-semibold mr-4">
              <p>Total Amount: ₹{totalAmount.toFixed(2)}</p>
              <small className="text-xs font-semibold text-gray-500">
                {numberToWords(totalAmount.toFixed(0))} Rupees
              </small>
            </div>
            <button
              onClick={() => setIsOpenView(true)}
              className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
              disabled={payoutList.length === 0}
            >
              Pay ({payoutList.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPayout;
